/**
 * @fileoverview Job Specifications Hooks
 * 
 * Provides hooks for fetching job specifications and RFQ quantity options.
 * These hooks support the entire RFQ, bidding, and award process by providing
 * specification details and quantity tier information.
 * 
 * **Available Hooks:**
 * 1. `useJobSpecifications` - Fetch specifications for a specific job
 * 2. `useRFQSpecificationQuantities` - Fetch quantity options for an RFQ
 * 
 * **Job Specifications:**
 * Specifications define the exact products or services being procured. Each
 * specification includes:
 * - Specification name and description
 * - Technical parameters (dimensions, materials, tolerances)
 * - Quality standards and certifications required
 * - Unit of measure (each, case, pallet, square feet, etc.)
 * - Specification order for consistent display
 * 
 * **Quantity Options:**
 * Clients can define multiple quantity tiers for each specification to enable
 * volume-based pricing. For example:
 * - Tier 1: 100 units at $10 each
 * - Tier 2: 500 units at $8 each
 * - Tier 3: 1000 units at $6 each
 * 
 * Vendors then bid on each tier, and clients select the optimal quantity/price
 * combination during the award process.
 * 
 * **Data Flow:**
 * 1. **Job Creation**: Client creates job with specifications
 * 2. **RFQ Setup**: Specifications linked to RFQ with quantity options
 * 3. **Vendor Bidding**: Vendors provide pricing for each quantity tier
 * 4. **Award Selection**: Client selects specific spec/quantity combinations
 * 5. **PO Creation**: Award selections become line items on purchase order
 * 
 * **Specification Structure:**
 * ```typescript
 * {
 *   id: 'spec-uuid',
 *   job_id: 'job-uuid',
 *   specification_name: 'Custom Machined Parts',
 *   specification_description: 'CNC machined aluminum brackets',
 *   unit_of_measure: 'each',
 *   specification_order: 1,
 *   technical_specs: {
 *     material: '6061-T6 Aluminum',
 *     tolerance: '±0.005 inches',
 *     finish: 'Anodized'
 *   }
 * }
 * ```
 * 
 * **Quantity Options Structure:**
 * ```typescript
 * {
 *   id: 'option-uuid',
 *   rfq_id: 'rfq-uuid',
 *   specification_id: 'spec-uuid',
 *   quantity_option: 1000,
 *   unit: 'each'
 * }
 * ```
 * 
 * @module useJobSpecifications
 * 
 * @example
 * ```tsx
 * // Fetch and display job specifications
 * function JobSpecsList({ jobId }) {
 *   const { specifications, loading, error } = useJobSpecifications(jobId);
 * 
 *   if (loading) return <Spinner />;
 *   if (error) return <ErrorMessage error={error} />;
 * 
 *   return (
 *     <div>
 *       {specifications.map(spec => (
 *         <div key={spec.id}>
 *           <h3>{spec.specification_name}</h3>
 *           <p>{spec.specification_description}</p>
 *           <Badge>{spec.unit_of_measure}</Badge>
 *         </div>
 *       ))}
 *     </div>
 *   );
 * }
 * ```
 * 
 * @example
 * ```tsx
 * // Fetch RFQ quantity options for bidding
 * function BidForm({ rfqId }) {
 *   const { specifications } = useJobSpecifications(jobId);
 *   const { quantities, loading } = useRFQSpecificationQuantities(rfqId);
 * 
 *   return (
 *     <form>
 *       {specifications.map(spec => {
 *         const specQuantities = quantities.filter(
 *           q => q.specification_id === spec.id
 *         );
 *         
 *         return (
 *           <div key={spec.id}>
 *             <h4>{spec.specification_name}</h4>
 *             {specQuantities.map(qty => (
 *               <div key={qty.id}>
 *                 <label>Price for {qty.quantity_option} {qty.unit}:</label>
 *                 <input type="number" name={`price_${qty.id}`} />
 *               </div>
 *             ))}
 *           </div>
 *         );
 *       })}
 *     </form>
 *   );
 * }
 * ```
 * 
 * @example
 * ```tsx
 * // Award selection with spec/quantity mapping
 * function AwardSelection({ jobId, winningBidId }) {
 *   const { specifications } = useJobSpecifications(jobId);
 *   const [selectedQuantities, setSelectedQuantities] = useState({});
 * 
 *   const handleAward = async () => {
 *     const selections = specifications.map(spec => ({
 *       specification_id: spec.id,
 *       quantity_option: selectedQuantities[spec.id],
 *       // ... price information from bid line items
 *     }));
 *     
 *     await awardJob(jobId, winningBidId, selections);
 *   };
 * 
 *   return (
 *     <div>
 *       {specifications.map(spec => (
 *         <QuantitySelector
 *           key={spec.id}
 *           spec={spec}
 *           onChange={(qty) => setSelectedQuantities({
 *             ...selectedQuantities,
 *             [spec.id]: qty
 *           })}
 *         />
 *       ))}
 *       <Button onClick={handleAward}>Award Job</Button>
 *     </div>
 *   );
 * }
 * ```
 */

import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';

// Use the actual Supabase types for job specifications
export const useJobSpecifications = (jobId: string) => {
  const [specifications, setSpecifications] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchSpecifications = async () => {
      if (!jobId) {
        console.log('useJobSpecifications: No jobId provided');
        return;
      }

      try {
        setLoading(true);
        console.log('useJobSpecifications: Fetching specifications for jobId:', jobId);
        
        const { data, error } = await supabase
          .from('job_specifications')
          .select('*')
          .eq('job_id', jobId)
          .order('specification_order', { ascending: true });

        console.log('useJobSpecifications: Response data:', data);
        console.log('useJobSpecifications: Response error:', error);

        if (error) throw error;

        // Transform data to match expected interface
        const transformedSpecs = (data || []).map(spec => ({
          ...spec,
          specification_id: spec.id, // Add for compatibility with AwardSelectionModal
          name: spec.specification_name, // Map to expected field name
        }));

        setSpecifications(transformedSpecs);
        setError(null);
      } catch (err) {
        console.error('Error fetching job specifications:', err);
        setError(err instanceof Error ? err.message : 'Failed to fetch specifications');
      } finally {
        setLoading(false);
      }
    };

    fetchSpecifications();
  }, [jobId]);

  return { specifications, loading, error };
};

// Simplified hook for quantities - now fetches actual data
export const useRFQSpecificationQuantities = (rfqId: string) => {
  const [quantities, setQuantities] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchQuantities = async () => {
      if (!rfqId) return;
      try {
        setLoading(true);
        const { data, error } = await supabase
          .from('rfq_specification_quantity_options')
          .select('*')
          .eq('rfq_id', rfqId);
        if (error) throw error;
        setQuantities(data || []);
        setError(null);
      } catch (err) {
        console.error('Error fetching RFQ specification quantities:', err);
        setError(err instanceof Error ? err.message : 'Failed to fetch quantities');
      } finally {
        setLoading(false);
      }
    };

    fetchQuantities();
  }, [rfqId]);

  return { quantities, loading, error };
};